import { addSystem, appContext } from "@/shared/context.ts";
import { addEntity, removeEntity } from "@/shared/api/entity.ts";
import { prefabs } from "@/shared/data.ts";
import { KdTree } from "@/shared/util/KDTree.ts";
import { normalizeAngle, Point } from "@/shared/pathing/math.ts";
import { Entity } from "@/shared/types.ts";
import { raise } from "@/shared/util/raise.ts";
import { isNight } from "@/shared/dayNight.ts";

// Dedicated KD-tree for flowers
const flowerKd = new KdTree();
const flowerToPoint = new Map<Entity, Point>();
const pointToFlower = new Map<Point, Entity>();

// Track visited flowers per bee
const beeVisitedFlowers = new WeakMap<Entity, Set<Entity>>();

// Track which bee belongs to which flower (one bee per flower)
const flowerToBee = new Map<Entity, Entity>();

const flowerOffsets = [
  { x: -0.0086, y: -0.0109 },
  { x: -0.0833, y: 0.0788 },
  { x: 0.0546, y: 0.0588 },
];

const getFlowerLandingSpot = (flower: Entity) => {
  const offset =
    flowerOffsets[Math.floor(Math.random() * flowerOffsets.length)];
  const scale = flower.modelScale ?? 1;
  const facing = normalizeAngle(flower.facing ?? 0);
  const flip = facing < (Math.PI / 2) && facing > (Math.PI / -2);
  const angle = flip ? Math.PI - facing : facing + Math.PI;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const oy = flip ? -offset.y : offset.y;
  return {
    x: flower.position!.x + (offset.x * cos - oy * sin) * scale,
    y: flower.position!.y + (offset.x * sin + oy * cos) * scale,
  };
};

addSystem({
  props: ["prefab", "position"] as const,
  onAdd: (e) => {
    if (e.prefab === "flowers" && e.position) {
      flowerKd.add(e.position);
      flowerToPoint.set(e, e.position);
      pointToFlower.set(e.position, e);

      if (Math.random() < 0.10) spawnBeeAtFlower(e);
    }
  },
  onRemove: (e) => {
    if (e.prefab === "flowers") {
      const point = flowerToPoint.get(e);
      if (point) {
        flowerKd.delete(point);
        flowerToPoint.delete(e);
        pointToFlower.delete(point);
      }

      const bee = flowerToBee.get(e);
      if (bee) {
        removeEntity(bee);
        flowerToBee.delete(e);
      }
    }
  },
});

const spawnBeeAtFlower = (flower: Entity) => {
  if (!flower.position) return;
  if (flowerToBee.has(flower)) return; // Already has a bee

  const bee = addEntity({
    prefab: "bee",
    position: getFlowerLandingSpot(flower),
    facing: Math.random() * Math.PI * 2,
    movementSpeed:
      (prefabs.bee.movementSpeed ?? raise("bees lack movement speed!")) *
      (0.8 + Math.random() * 0.4),
    modelScale: 0.25 + Math.random() * 0.15,
    isEffect: true,
  });

  flowerToBee.set(flower, bee);

  // Initialize visited set with starting flower
  const visitedFlowers = new Set<Entity>([flower]);
  beeVisitedFlowers.set(bee, visitedFlowers);

  // Start looking for next flower
  scheduleNextFlowerVisit(bee);
};

const scheduleNextFlowerVisit = (bee: Entity) => {
  if (!appContext.current.entities.has(bee) || !bee.position) return;

  if (isNight()) return setTimeout(() => scheduleNextFlowerVisit(bee), 5000);

  const visitedFlowers = beeVisitedFlowers.get(bee);
  if (!visitedFlowers) return;

  // Find nearest unvisited flower
  const nearestPoint = flowerKd.nearest(
    bee.position.x,
    bee.position.y,
    (point: Point) => {
      const flower = pointToFlower.get(point);
      return flower
        ? !visitedFlowers.has(flower) && Math.random() < 0.5
        : false;
    },
  );

  let targetPoint = nearestPoint;

  if (!targetPoint) {
    // No unvisited flowers found, reset visited set and try again
    visitedFlowers.clear();
    targetPoint = flowerKd.nearest(
      bee.position.x,
      bee.position.y,
      () => Math.random() < 0.5,
    );
    if (!targetPoint) return; // No flowers at all
  }

  const targetFlower = pointToFlower.get(targetPoint);
  if (!targetFlower) return;

  visitedFlowers.add(targetFlower);

  const target = getFlowerLandingSpot(targetFlower);

  const dx = target.x - bee.position.x;
  const dy = target.y - bee.position.y;
  const distance = Math.sqrt(dx * dx + dy * dy);
  const heading = Math.atan2(dy, dx);

  // Update bee to fly to this flower
  bee.facing = heading;
  bee.order = { type: "walk", target, path: [target] };

  // Schedule next visit after reaching this flower
  const flightTime = distance / (bee.movementSpeed ?? 1.5);
  const hoverTime = 0.25 + Math.random() ** 2 * 4.75;
  setTimeout(() => {
    scheduleNextFlowerVisit(bee);
  }, (flightTime + hoverTime) * 1000);
};
